/* eslint-disable @typescript-eslint/no-explicit-any */
import { RGBAColor } from '@deck.gl/core';
import { SolidPolygonLayer } from '@deck.gl/layers';
import { Dispatch } from 'redux';
import { GeoJSON, LocationType, PropLocationItem, VarName } from '../../../services/api';
import { varNameBandParams } from '../../../utils/dataBandParams';
import {
  HoverType,
  hexToRgb,
  HoveredLoc,
  weightedColorWithBands,
  weightedColorWithoutBands,
} from '../mapHelpers';
import { themeProps } from '../../../styles/theme';
import { LocSensorStatusTree } from '../../../state/types';
import { ActionTypes } from '../../../state/actionTypes';
import { setCurrentLocation } from '../../../state/actions';

const defaultElevation = 7;
const defaultOpacity = 50;

//  Helper function to process a single feature with elevated data
function processFeature(feature: any, elevationPerLevel: number): any {
  // Only process features of type "area.floor"
  if (feature?.properties?.type === 'area.floor' && feature?.geometry?.type === 'MultiPolygon') {
    const elevation = feature.properties.level * elevationPerLevel;
    if (typeof elevation !== 'number' || Number.isNaN(elevation)) {
      // Decide on fallback behavior: return feature unchanged? Use default elevation?
      return feature;
    }

    // Create a new feature object to avoid modifying the parameter
    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: feature.geometry.coordinates.map((polygon: any[]) =>
          polygon.map((ring: any[]) =>
            ring.map((point: number[]) => {
              // If the point has only x,y coordinates
              if (point.length === 2) {
                // Add the z-coordinate (elevation)
                return [...point, elevation];
              }
              // If it already has a z-coordinate, update it
              const newPoint = [...point];
              newPoint[2] = elevation;
              return newPoint;
            })
          )
        ),
      },
    };
  }
  // Return the feature unchanged if it's not a floor area
  return feature;
}

// Adds elevation to GeoJSON features of type "area.floor" by adding a z-coordinate to each point in the geometry.
function addElevationToFloors(geojson: GeoJSON, elevationPerLevel: number): GeoJSON {
  // Create a deep copy to avoid modifying the original data
  const result = JSON.parse(JSON.stringify(geojson));
  // Process a feature collection
  if (result.type === 'FeatureCollection' && Array.isArray(result.features)) {
    result.features = result.features.map((feature: any) =>
      processFeature(feature, elevationPerLevel)
    );
  }
  // Process a single feature
  else if (result.type === 'Feature') {
    return processFeature(result, elevationPerLevel);
  }
  return result;
}

const get3DBuildingLayer = (
  currentLocation: string,
  childLocations: PropLocationItem[],
  activeMarker: VarName,
  locSensorStatusData: LocSensorStatusTree,
  floorPlan: GeoJSON | undefined,
  motionThreshold: [number, number],
  locationsById: Map<string, PropLocationItem>,
  hoveredLoc?: HoveredLoc,
  setHoveredLoc?: (value: HoveredLoc | undefined) => void,
  dispatch?: Dispatch<ActionTypes>
) => {
  if (!floorPlan) return null;
  const onlineVarData = locSensorStatusData.onlineData.get(activeMarker) ?? [];
  const hoveredLocId = hoveredLoc?.id;

  const allFloorNames = floorPlan.features.map((f) => f.properties.name);

  const getHoveredFloor = () => {
    // check if hovered loc is floorLevel or client level
    const isFloorLevel =
      childLocations.find((item) => item.id === hoveredLocId)?.type === LocationType.FloorLevel;
    let hoveredFloors = [hoveredLocId]; // default assumption as floor
    if (!isFloorLevel) {
      // find floors nested inside where starts with hovereLoc name and ends with floorPlan floor name
      hoveredFloors = Array.from(locationsById.values())
        .filter(
          (loc) =>
            loc.id.startsWith(`${hoveredLocId}#`) &&
            allFloorNames.some((name) => loc.id.endsWith(name))
        )
        .map((loc) => loc.id);
    }
    return hoveredFloors;
  };

  const getFillColor = (item: any) => {
    const locName = item.properties?.name;
    if (!locName) {
      return hexToRgb(themeProps.colors.mediumGrey, 255);
    }

    // check if locName (eg: FL-04) from floorPlan exists from hovered loc (eg: ['#CL-Test#BLD-Demo#BLD-Dem0#FL-04'])
    const isHoveredLoc = getHoveredFloor().some((floor) => floor?.split('#').pop() === locName);
    const opacity = hoveredLocId && !isHoveredLoc ? defaultOpacity : 255;

    const locId =
      Array.from(locationsById.values()).find(
        (loc) => loc.id.startsWith(currentLocation) && loc.id.endsWith(item.properties.name)
      )?.id ?? '';

    // use band if avaiable, if not generate min/max value and calculate color weight based on total
    const fillColor = varNameBandParams[activeMarker]
      ? weightedColorWithBands(activeMarker, onlineVarData, locId, opacity, motionThreshold)
      : weightedColorWithoutBands(locId, childLocations, onlineVarData, opacity);
    return fillColor as RGBAColor;
  };

  const getLineColor = (item: any) => {
    const locName = item.properties?.name;
    // check if locName (eg: FL-04) from floorPlan exists from hovered loc (eg: ['#CL-Test#BLD-Demo#BLD-Dem0#FL-04'])
    const isHoveredLoc = getHoveredFloor().some((floor) => floor?.split('#').pop() === locName);

    const opacity = hoveredLocId && !isHoveredLoc ? defaultOpacity : 255;
    return [0, 0, 0, opacity] as RGBAColor; // default to black
  };

  const elevatedData = addElevationToFloors(floorPlan, defaultElevation);

  const getMapHoveredLoc = (locName: string) => {
    const locId =
      Array.from(locationsById.values()).find(
        (loc) => loc.id.startsWith(currentLocation) && loc.id.endsWith(locName)
      )?.id ?? '';

    let hoveredId;
    // check if hovered loc exists in current loc child location
    const locExists = childLocations.find((item) => item.id === locId);
    if (locExists) hoveredId = locId;
    // if not, could be nested inside client level, so pick childLoc starting with current location
    else hoveredId = locId.slice(0, locId.indexOf('#', currentLocation.length + 1));

    return hoveredId;
  };

  return new SolidPolygonLayer({
    id: 'building',
    data: elevatedData.features,
    extruded: true,
    wireframe: true,
    getPolygon: (d: any) => d.geometry.coordinates[0],
    getElevation: 5,
    getFillColor: (d: any) => getFillColor(d),
    getLineColor: (d: any) => getLineColor(d),
    pickable: true,
    onHover: (obj: any) => {
      if (setHoveredLoc) {
        if (obj?.picked) {
          const objData = obj.object;
          const locName: string = objData.properties?.name;
          if (locName) {
            const hoveredId = getMapHoveredLoc(locName);
            if (hoveredId) setHoveredLoc({ id: hoveredId, hoveredType: HoverType.map });
          }
        } else setHoveredLoc(undefined);
      }
    },
    onClick: (obj: any) => {
      if (obj?.picked) {
        const objData = obj.object;
        const locName: string = objData.properties?.name;
        if (locName && dispatch) {
          const hoveredId = getMapHoveredLoc(locName);
          if (hoveredId) dispatch(setCurrentLocation(hoveredId));
        }
        if (setHoveredLoc) setHoveredLoc(undefined);
      }
    },
    parameters: {
      depthMask: !hoveredLocId, // Hack to make solid layer more visible under transparent
    },
  });
};

export default get3DBuildingLayer;
